import React, { useMemo, useState } from "react";
import {
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
} from "reactstrap";
import { useAccess } from "features/Authentication/hooks/useAccess";
import { Permissions } from "features/RolesAndPermissions/constants/permission.constants";
import type { WorkOrder } from "features/WorkOrders/types/work-order.types";
import {
  getWorkOrderLifecycleVisibility,
  workOrderLifecycleAnyVisible,
} from "features/WorkOrders/utils/work-order-lifecycle.utils";
import {
  invoiceHasMoneyMoved,
  useSourceActiveInvoice,
} from "features/Invoices/hooks/useSourceActiveInvoice";
import { InvoiceSourceType } from "features/Invoices/constants/invoice.constants";
import WorkOrderLifecycleModals, {
  type WorkOrderLifecycleIntent,
} from "./WorkOrderLifecycleModals";

type Props = {
  workOrder: WorkOrder;
  onSuccess: () => void | Promise<void>;
};

export default function WorkOrderLifecycleButtons({
  workOrder,
  onSuccess,
}: Props) {
  const { can } = useAccess();
  const [intent, setIntent] = useState<WorkOrderLifecycleIntent | null>(null);

  const vis = useMemo(
    () => getWorkOrderLifecycleVisibility(workOrder, can),
    [workOrder, can],
  );

  const canCancelInvoice = can(Permissions.INVOICE.CANCEL);

  const {
    activeInvoice,
    loading: activeInvoiceLoading,
    refetch: refetchActiveInvoice,
  } = useSourceActiveInvoice({
    sourceType: InvoiceSourceType.WORK_ORDER,
    sourceId: workOrder.id,
    enabled: vis.regenerateInvoice,
  });
  const moneyMoved = activeInvoice != null && invoiceHasMoneyMoved(activeInvoice);

  const showRegenerateInvoice =
    vis.regenerateInvoice &&
    !activeInvoiceLoading &&
    (activeInvoice == null || moneyMoved);
  const showCancelInvoice =
    vis.regenerateInvoice &&
    !activeInvoiceLoading &&
    activeInvoice != null &&
    !moneyMoved &&
    canCancelInvoice;

  if (!workOrderLifecycleAnyVisible(vis)) {
    return null;
  }

  const btnClass =
    "btn btn-sm d-inline-flex align-items-center gap-1 flex-shrink-0";

  const hasDropdown = vis.cancel;

  return (
    <>
      <div className="hstack gap-2 flex-wrap align-items-center">
        {vis.start ? (
          <button
            type="button"
            className={`${btnClass} btn-success`}
            onClick={() => setIntent({ kind: "start" })}
          >
            <i className="ri-play-circle-line align-bottom" />
            Start work
          </button>
        ) : null}
        {vis.hold ? (
          <button
            type="button"
            className={`${btnClass} btn-warning`}
            onClick={() => setIntent({ kind: "hold" })}
          >
            <i className="ri-pause-circle-line align-bottom" />
            Hold
          </button>
        ) : null}
        {vis.resume ? (
          <button
            type="button"
            className={`${btnClass} btn-success`}
            onClick={() => setIntent({ kind: "resume" })}
          >
            <i className="ri-play-circle-line align-bottom" />
            Resume
          </button>
        ) : null}
        {vis.approveForBilling ? (
          <button
            type="button"
            className={`${btnClass} btn-success`}
            onClick={() => setIntent({ kind: "approveForBilling" })}
          >
            <i className="ri-check-double-line align-bottom" />
            Approve for billing
          </button>
        ) : null}
        {vis.submitBilling ? (
          <button
            type="button"
            className={`${btnClass} btn-primary`}
            onClick={() => setIntent({ kind: "submitBilling" })}
          >
            <i className="ri-bill-line align-bottom" />
            Submit billing
          </button>
        ) : null}
        {vis.close ? (
          <button
            type="button"
            className={`${btnClass} btn-dark`}
            onClick={() => setIntent({ kind: "close" })}
          >
            <i className="ri-lock-line align-bottom" />
            Close
          </button>
        ) : null}
        {showRegenerateInvoice ? (
          <button
            type="button"
            className={`${btnClass} btn-outline-primary`}
            onClick={() => setIntent({ kind: "regenerateInvoice" })}
          >
            <i className="ri-refresh-line align-bottom" />
            Regenerate invoice
          </button>
        ) : null}
        {showCancelInvoice ? (
          <button
            type="button"
            className={`${btnClass} btn-outline-danger`}
            onClick={() =>
              activeInvoice &&
              setIntent({
                kind: "cancelActiveInvoice",
                invoiceId: activeInvoice.id,
              })
            }
            title="An active invoice already exists for this work order with nothing collected against it yet — cancel it before a new one can be generated."
          >
            <i className="ri-close-circle-line align-bottom" />
            Cancel invoice
          </button>
        ) : null}
        {hasDropdown ? (
          <UncontrolledDropdown>
            <DropdownToggle
              type="button"
              className="btn btn-light btn-sm border"
            >
              <i className="ri-more-2-fill align-middle" />
            </DropdownToggle>
            <DropdownMenu end>
              {vis.cancel ? (
                <DropdownItem
                  className="text-danger"
                  onClick={() => setIntent({ kind: "cancel" })}
                >
                  <i className="ri-close-circle-line align-bottom me-2" />
                  Cancel work order
                </DropdownItem>
              ) : null}
            </DropdownMenu>
          </UncontrolledDropdown>
        ) : null}
      </div>

      <WorkOrderLifecycleModals
        workOrder={workOrder}
        intent={intent}
        onFinished={() => setIntent(null)}
        onSuccess={async () => {
          await onSuccess();
          refetchActiveInvoice();
        }}
      />
    </>
  );
}
